<script setup lang="ts">
import { computed } from 'vue'
import VIcon from '../VIcon/VIcon.vue'
import type { IComponentPrimitives } from '@/ui/ts/types/IComponentPrimitives'
import { ICON_SIZES, SIZES, type TIconSizes, type TSizes } from '@/ui/ts/types/Sizes'

export type TStatus = 'default' | 'success' | 'error' | 'warning'

interface IProps extends Omit<IComponentPrimitives, 'dark' | 'id' | 'size'> {
    text: string
    status?: TStatus
    icon?: boolean
    size?: TSizes
    background?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
    text: 'Validation Message',
    status: 'default',
    icon: false,
    size: SIZES.XSMALL,
    background: false,
})

const statusClass = computed(() => {
    switch (props.status) {
        case 'success':
            return /* tw */ `text-validation-success ${props.background && 'bg-validation-success-light'}`
        case 'error':
            return /* tw */ `text-validation-danger ${props.background && 'bg-validation-danger-light'}`
        case 'warning':
            return /* tw */ `text-validation-warning ${props.background && 'bg-validation-warning-light'}`
        default:
            return props.background
                ? /* tw */ 'text-neutral-secondary bg-neutral-secondary'
                : /* tw */ 'text-neutral-tertiary'
    }
})

const statusIcon = computed(() => {
    switch (props.status) {
        case 'success':
            return 'validation-success'
        case 'error':
            return 'validation-error'
        case 'warning':
            return 'validation-warning'
        default:
            return ''
    }
})

interface TSizeOptionClasses {
    container: string
    iconSize: TIconSizes
    paragraph: string
}

const sizes: Record<TSizes, TSizeOptionClasses> = {
    [SIZES.XSMALL]: {
        container: /* tw */ `${props.background && 'rounded-container-xs px-sm py-xs'}`,
        iconSize: ICON_SIZES.XSMALL,
        paragraph: /* tw */ 'paragraph-xs',
    },
    [SIZES.SMALL]: {
        container: /* tw */ `${props.background && 'rounded-container-sm px-base py-xs'}`,
        iconSize: ICON_SIZES.SMALL,
        paragraph: /* tw */ 'paragraph-sm',
    },
    [SIZES.MEDIUM]: {
        container: /* tw */ `${props.background && 'rounded-container-md px-base py-xs'}`,
        iconSize: ICON_SIZES.MEDIUM,
        paragraph: /* tw */ 'paragraph-md',
    },
    [SIZES.LARGE]: {
        container: /* tw */ `${props.background && 'rounded-container-lg px-lg py-sm'}`,
        iconSize: ICON_SIZES.MEDIUM,
        paragraph: /* tw */ 'paragraph-lg',
    },
}
const size = sizes[props.size]
</script>

<template>
    <div
        :class="[statusClass, size.container]"
        class="flex items-center gap-1"
        :data-test="`validation-message-${dataTest}`"
    >
        <VIcon
            v-if="icon && status !== 'default'"
            :size="size.iconSize"
            :name="statusIcon"
        />
        <p
            v-html-gpp="text"
            class="line-clamp-2"
            :class="[size.paragraph]"
        />
    </div>
</template>
